import styled, { css } from 'styled-components';
import { ButtonProps } from './button';

export const ButtonContent = styled.div<{ $forIcon?: boolean }>`
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0;
  white-space: nowrap;
  padding: 10px;
  img {
    object-fit: cover;
  }
  @media (max-width: 768px) {
    padding: ${({ $forIcon }) => $forIcon && '0 8px'};
  }
`;

export const StyledButton = styled.button<{
  $variant: ButtonProps['variant'];
  $fill?: boolean;
  $flex?: string;
}>`
  position: relative;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  flex: 1 0 0;
  border-radius: 8px;
  border: none;
  cursor: pointer;

  ${({ $variant }) => buttonVariants[$variant] || buttonVariants.gray};
  ${({ $fill }) => $fill && 'width: 100%;'};
  ${({ $flex }) => $flex && `flex: ${$flex};`};
`;


const commonStates = css`
  &:hover {
    opacity: 0.9;
  }

  &:active {
    opacity: 0.9;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const buttonVariants: Record<ButtonProps['variant'], ReturnType<typeof css>> = {
  primary: css`
    color: #edeff5;
    background: #3d383f;
    overflow: hidden;
    ${commonStates}
    
    &:active {
      background: #3d383f;
    }
  `,

  midnightPurple: css`
    color: #fff;
    background: #4A4A73;
    overflow: hidden;
    padding: 0 8px;
    ${commonStates}
  `,

  orangeGradient: css`
    color: #edeff5;
    background: linear-gradient(90deg, #FF9500 0%, #FF5E00 100%);
    overflow: visible;
    ${commonStates}
  `,

  black: css`
    color: #fff;
    background: #131315;
    overflow: hidden;
    padding: 0 8px;
    ${commonStates}
  `,

  gray: css`
    color: #edeff5;
    background: #3D3C41;
    overflow: visible;
    ${commonStates}
  `,
  
  grayBlack: css`
  color: #edeff5;
  background: #625A63;
  overflow: visible;
  ${commonStates}
`,

  secondary: css`
    color: #edeff5;
    background: #27262B;
    overflow: hidden;
    ${commonStates}
    
    &:active {
      background: #3d383f;
    }
  `,

  pink: css`
    background: linear-gradient(225deg, #BF5AF2 0%, #0B84FF 100%), 
               linear-gradient(0deg, #0A84FF 0%, #0A84FF 100%), 
               #30D158;
    ${commonStates}
  `,

  'blue-auth': css`
    background-color: #2672FF;
    ${commonStates}
  `,

  yellow: css`
    background: linear-gradient(246deg, #FF9F0A 0%, #FFD60A 68.98%),
               linear-gradient(90deg, #FF375F 0%, #BA1B3B 100%),
               linear-gradient(0deg, #FF375F 0%, #FF375F 100%),
               linear-gradient(246deg, #B81500 0%, #D95E00 68.98%),
               #2247C6;
    color: #000000;
    ${commonStates}
  `,

  yellowGradient: css`
    color: #2A2A2A;
    border-radius: 9.344px;
    background: linear-gradient(246deg, #FF9F0A 0%, #FFD60A 68.98%), 
               linear-gradient(90deg, #FF375F 0%, #BA1B3B 100%), 
               linear-gradient(0deg, #FF375F 0%, #FF375F 100%), 
               linear-gradient(246deg, #B81500 0%, #D95E00 68.98%), 
               #2247C6;
    ${commonStates}
  `,

  greenBlueGradient: css`
    background: linear-gradient(89deg, #34C759 0.66%, #007AFF 116.39%);
    ${commonStates}
  `,

  blue: css`
    background: linear-gradient(126deg, #5BDBFF 1.05%, rgba(91, 219, 255, 0.00) 46.67%), #2672FF;
    ${commonStates}
    
    &:hover {
      background-image: url(/images/background/spin-button.svg);
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }
    
    &:disabled:hover {
      background: linear-gradient(126deg, #5BDBFF 1.05%, rgba(91, 219, 255, 0.00) 46.67%), #2672FF;
    }
  `,

  green: css`
    background: linear-gradient(103deg, #60FF65 -8.65%, rgba(96, 255, 101, 0.00) 26.92%), #2AA30A;
    box-shadow: 0px 0px 8px 0px #5CFF69 inset;
    ${commonStates}
  `,

  orange: css`
    background: linear-gradient(90deg, rgba(204, 127, 8, 0.00) 0%, #B65E00 100%), #FF9F0A;
    ${commonStates}
  `,

  success: css`
    background-color: green;
    color: white;
    ${commonStates}
  `,

  danger: css`
    background-color: rgba(208, 51, 49, 0.50);
    color: white;
    ${commonStates}
  `,

  transparent: css`
    background-color: transparent;
    ${commonStates}
  `,

  wishlist: css`
    background-color: rgba(65, 77, 107, 0.4);
    ${commonStates}
  `,

  notify: css`
    background-color: #153297;
    ${commonStates}
  `,

  bonusBlue: css`
    background: linear-gradient(90deg, #0B84FF 0%, #074F99 100%), #0B84FF;
    ${commonStates}
  `,

  bonusYellow: css`
    background: linear-gradient(90deg, rgba(204, 127, 8, 0.00) 0%, #B65E00 100%), #FF9F0A;
    ${commonStates}
  `,
};